/**
 * @fileoverview Syndicator Showdown — Player-Facing Value Badge Metadata
 * @module utils/valueBadge
 *
 * Single source of truth for how a runner's value_engine_v1 classification
 * is presented to players. The backend emits BOTH:
 *   - runner.valueLabel  (8-way, e.g. GREAT_VALUE / SPECULATIVE_VALUE / ...)
 *     Used internally (e.g. stableGenerator's Value mode) where the extra
 *     nuance matters, but never shown to players directly.
 *   - runner.valueBadge  (6-way, e.g. BARGAIN / VALUE / FAIR / PRICEY / RISK)
 *     ALWAYS what the UI should render — matches the terminology players
 *     see across the rating list and the Buy card. ("Pricey" replaces the
 *     earlier "Premium" wording, which read as a quality signal rather than
 *     "probably above fair value".)
 *
 * Colours mirror runner.valueBadgeMeta from the backend, duplicated here so
 * the UI has a rendering fallback even if a runner document predates the
 * value engine upgrade (no valueBadgeMeta stored yet).
 */

export const VALUE_BADGE_META = {
  UNKNOWN: { label: 'Unknown', colour: '#9ca3af', bg: 'rgba(148,163,184,0.15)' },
  BARGAIN: { label: 'Bargain', colour: '#22c55e', bg: 'rgba(34,197,94,0.16)' },
  VALUE:   { label: 'Value',   colour: '#4ade80', bg: 'rgba(74,222,128,0.14)' },
  FAIR:    { label: 'Fair',    colour: '#60a5fa', bg: 'rgba(96,165,250,0.14)' },
  PRICEY:  { label: 'Pricey',  colour: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  RISK:    { label: 'Risk',    colour: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

/**
 * Resolves display metadata for a runner's value badge, preferring the
 * backend-supplied valueBadgeMeta (kept in sync with value_engine_v1) and
 * falling back to the local lookup table above.
 *
 * @param {string} valueBadge      - UNKNOWN | BARGAIN | VALUE | FAIR | PRICEY | RISK
 * @param {object} [valueBadgeMeta] - optional backend-supplied override
 */
export function getValueBadgeMeta(valueBadge, valueBadgeMeta) {
  return valueBadgeMeta || VALUE_BADGE_META[valueBadge] || VALUE_BADGE_META.UNKNOWN;
}

export default VALUE_BADGE_META;
